import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { DISPLAY_VERSION } from "../../environments";
import { AppVersionInfo, appVersionToString, areVersionsEqual } from "./appVersionTypes";
import { CombinedLabeling, Labeling, WorkspaceLabeling } from "./labeling";


export type AppVersionSliceState = {
    /** Current version of the app, or null if not defined or detected. */
    version: AppVersionInfo | null;
    /** True if there's a new version of the app available on the server, false otherwise. */
    isNewAppVersionAvailable: boolean;
    /** True if new version alert dialog is snoozed (not shown), false otherwise. */
    isNewVersionAlertSnoozed: boolean;

    /** Labels for this application. */
    productLabels: Labeling[] | null,

    /** An error that occurred when trying to retrieve labeling, or null.*/
    productLabelError: string | null,

    /** Workspace labeling. */
    workspaceLabeling: WorkspaceLabeling | null;

    /** An error that occurred when trying to retrieve workspace labeling, or null.*/
    workspaceLabelError: string | null;

    /** Backend version as a user-presentable string */
    backendVersionAsString: string | null;
};


export const initialState: AppVersionSliceState = {
    version: null,
    isNewAppVersionAvailable: false,
    isNewVersionAlertSnoozed: false,
    productLabels: null,
    productLabelError: null,
    backendVersionAsString: null,
    workspaceLabeling: null,
    workspaceLabelError: null,
};

/** Redux store slice for handling with app version (which is downloaded from the server, not 
 * bundled in the source code). App version is used to track if there's a new version of the client
 * code available on the server. User can be shown a dialog to tell them to refresh their page
 * as needed. */
const appVersionSlice = createSlice({
    name: 'appVersion',
    initialState,
    reducers: {
        /** Sets current app version (from a data file downloaded from server). App version in state is not overwritten
         * if it has already been set once. In that case, check if the received app version is for a new version.
         * If so, mark that a new app version is available.
         * @param action payload: AppVersionInfo object matching the app version we're trying to set.
         */
        appVersionSet(state, action: PayloadAction<AppVersionInfo>) {
            const appVersion = action.payload;
            if (state.version === null) {
                /** Print current version to developer console on receive to make debugging easier. */
                console.log(`App version: ${appVersionToString(appVersion)}, display version: ${DISPLAY_VERSION}`);
                console.log(appVersion);
                state.version = appVersion;
            } else {
                if (!areVersionsEqual(state.version, appVersion)) {
                    state.isNewAppVersionAvailable = true;
                }
            }
        },

        /** Signals new version alert as snoozed */
        newVersionAlertSnoozed() {
            // this is an empty action and is only used for signalling in sagas -- see appVersionSagas.ts for implementation
            // TODO: this could probably also be done with a thunk instead of sagas
        },

        /** Sets new version alert snooze on (true) or off (false).
         * @param action payload: true if new version alert should be snoozed, false otherwise.
         */
        newVersionAlertSnoozeSet(state, action: PayloadAction<boolean>) {
            state.isNewVersionAlertSnoozed = action.payload;
        },

        /** Signals that product labeling should be fetched. */
        labelingFetched() {
            // this is an empty action and is only used for signalling in sagas -- see appVersionSagas.ts for implementation
            // TODO: this could probably also be done with a thunk instead of sagas
        },

        /** Signals that workspace labeling should be fetched. */
        workspaceLabelingFetched() {
            // this is an empty action and is only used for signalling in sagas -- see appVersionSagas.ts for implementation
        },

        /**
         * Sets product labels to supplied values.
         * @param action.combinedLabeling The labels to use.
         * @param action.error An optional error message.
         */
        labelingSet(state, action: PayloadAction<{ combinedLabeling: CombinedLabeling | null, error?: string | undefined }>) {
            const { combinedLabeling, error } = action.payload;

            if (combinedLabeling !== null) {
                state.productLabels = combinedLabeling.labels;
                state.backendVersionAsString = combinedLabeling.backendVersionAsString || null;
            }

            state.productLabelError = error !== undefined ? error : null;
        },

        /** Sets workspace labeling to supplied values. */
        workspaceLabelingSet(state, action: PayloadAction<{ labeling: WorkspaceLabeling | null, error?: string | undefined }>) {
            const { labeling, error } = action.payload;
            state.workspaceLabeling = labeling;
            state.workspaceLabelError = error !== undefined ? error : null;
        }
    },
    selectors: {
        selectAppVersion: (state) => state.version,
    }
});

export const { appVersionSet, newVersionAlertSnoozed, newVersionAlertSnoozeSet, labelingFetched, labelingSet, workspaceLabelingFetched, workspaceLabelingSet } = appVersionSlice.actions;

export const { getInitialState, selectors: appVersionSelectors } = appVersionSlice;

export default appVersionSlice.reducer;
